import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Settings,
  Sliders,
  RefreshCw,
  Loader2,
  Layout,
  Map,
  Bell,
  Globe,
  Navigation,
  RotateCcw,
  Save,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Video,
} from 'lucide-react';

const categoryIcons = {
  page: Layout,
  app_tabs: Layout,
  global_settings_tabs: Settings,
  module: Settings,
  map_provider: Globe,
  map_feature: Map,
  live_tracking_controls: Navigation,
  notification_channels: Bell,
  analytics_panels: Layout,
  video_enhancements: Video,
  integration: Bell,
};

const categoryLabels = {
  page: 'Pages',
  app_tabs: 'App Tabs',
  global_settings_tabs: 'Global Settings Tabs',
  module: 'Modules & Automation',
  map_provider: 'Map Providers',
  map_feature: 'Map Features',
  live_tracking_controls: 'Live Tracking Controls',
  notification_channels: 'Notification Channels',
  analytics_panels: 'Analytics Panels',
  video_enhancements: 'Video Enhancements',
  integration: 'Integrations',
};

const categoryDescriptions = {
  page: 'Control which pages are visible in the navigation',
  app_tabs: 'Control the user-facing tabs and major in-page sections across the app',
  global_settings_tabs: 'Control which tabs are available inside Admin Panel > System Settings',
  map_provider: 'Control which map providers are available to users',
  map_feature: 'Enable or disable map-specific features and live map enhancements',
  live_tracking_controls: 'Control Live Tracking panel sections and device interaction tools',
  notification_channels: 'Control which notification delivery channels are globally available',
  analytics_panels: 'Control optional panels and workspaces inside Analytics',
  video_enhancements: 'Premium video features inside Event Videos and Remote Media: trail playback, evidence sharing, and related modal extras',
  module: 'Enable or disable global modules and future automation capabilities',
  integration: 'Control external integration capabilities available platform-wide',
};

const tabOrder = [
  'page',
  'app_tabs',
  'global_settings_tabs',
  'map_provider',
  'map_feature',
  'live_tracking_controls',
  'notification_channels',
  'analytics_panels',
  'video_enhancements',
  'module',
  'integration',
];

const globalSettingsFlags = new Set([
  'global_comms_providers',
  'global_billing_plans',
  'global_maps_geocoding',
  'global_localization',
  'global_api_webhooks',
  'global_fuel_cards',
  'global_sim_provider',
  'global_accounting',
  'global_maintenance',
  'global_data_retention',
  'global_security',
]);

const liveTrackingControlFlags = new Set([
  'live_tracking_driver',
  'live_tracking_staff',
  'live_tracking_passengers',
  'live_tracking_telemetry_data',
  'live_tracking_commands',
  'live_tracking_events',
  'live_tracking_edit_asset',
  'live_tracking_edit_icon',
  'share_position',
  'generate_link',
]);

const notificationChannelFlags = new Set([
  'notification_email',
  'notification_sms',
  'notification_push',
  'event_webhook_commands',
]);

const analyticsPanelFlags = new Set([
  'analytics_history_forensic_panel',
]);

const videoEnhancementsFlags = new Set([
  'media_share_event_evidence',
  'media_video_show_trail_map',
]);

const lockedAlwaysOnFlags = new Set([
  'admin_panel_page',
]);

const getFeatureGroup = (flag) => {
  if (videoEnhancementsFlags.has(flag.name)) return 'video_enhancements';
  if (globalSettingsFlags.has(flag.name)) return 'global_settings_tabs';
  if (notificationChannelFlags.has(flag.name)) return 'notification_channels';
  if (liveTrackingControlFlags.has(flag.name)) return 'live_tracking_controls';
  if (analyticsPanelFlags.has(flag.name)) return 'analytics_panels';
  if (flag.category === 'subpage') return 'app_tabs';
  return flag.category;
};

export const FeatureManagement = () => {
  const { api } = useAuth();
  const { refetch: refetchFeatures } = useFeatureFlags();
  const [flags, setFlags] = useState([]);
  const [pendingFeatureAction, setPendingFeatureAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [activeTab, setActiveTab] = useState('page');

  const fetchFlags = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/features/all');
      setFlags(response.data.flags || []);
    } catch (error) {
      console.error('Failed to fetch feature flags:', error);
      toast.error('Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const flagsByCategory = useMemo(() => {
    const grouped = {};
    for (const flag of flags) {
      const group = getFeatureGroup(flag);
      if (!categoryLabels[group]) continue;
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(flag);
    }
    for (const key of Object.keys(grouped)) {
      grouped[key] = grouped[key].sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''));
    }
    return grouped;
  }, [flags]);

  const orderedCategories = useMemo(() => {
    return tabOrder.filter(category => (flagsByCategory[category] || []).length > 0);
  }, [flagsByCategory]);

  useEffect(() => {
    if (!orderedCategories.length) return;
    if (!orderedCategories.includes(activeTab)) {
      setActiveTab(orderedCategories[0]);
    }
  }, [orderedCategories, activeTab]);

  const handleToggle = (flagName, currentValue) => {
    if (lockedAlwaysOnFlags.has(flagName)) {
      toast.info('Admin Panel is a protected core page and must stay on');
      return;
    }
    setPendingChanges(prev => ({
      ...prev,
      [flagName]: !currentValue
    }));
  };

  const isChanged = (flagName, currentValue) => {
    return pendingChanges.hasOwnProperty(flagName);
  };

  const getCurrentValue = (flag) => {
    if (lockedAlwaysOnFlags.has(flag.name)) return true;
    if (pendingChanges.hasOwnProperty(flag.name)) {
      return pendingChanges[flag.name];
    }
    return flag.is_enabled;
  };

  const handleSaveChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    try {
      const updates = Object.entries(pendingChanges).map(([name, is_enabled]) => ({
        name,
        is_enabled
      }));

      await api.post('/features/bulk-update', updates);
      toast.success(`Updated ${updates.length} feature flags`);
      setPendingChanges({});
      await fetchFlags();
      await refetchFeatures();
    } catch (error) {
      console.error('Failed to save changes:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = async () => {
    setPendingFeatureAction({ title: 'Reset Feature Flags', message: 'Are you sure you want to reset all feature flags to defaults? This cannot be undone.', action: async () => {
      setSaving(true);
      try {
        await api.post('/features/reset');
        toast.success('Feature flags reset to defaults');
        setPendingChanges({});
        await fetchFlags();
        await refetchFeatures();
      } catch (error) {
        console.error('Failed to reset flags:', error);
        toast.error('Failed to reset feature flags');
      } finally {
        setSaving(false);
      }
    }});
  };

  const renderFeatureCard = (flag) => {
    const Icon = categoryIcons[flag.category] || Settings;
    const isEnabled = getCurrentValue(flag);
    const hasChange = isChanged(flag.name, flag.is_enabled);
    const isLockedAlwaysOn = lockedAlwaysOnFlags.has(flag.name);
    const isParentDisabled = flag.parent_feature && !getCurrentValue(flags.find(f => f.name === flag.parent_feature) || { is_enabled: true });

    return (
      <div
        key={flag.id}
        className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
          hasChange 
            ? 'border-amber-500 bg-amber-50 dark:bg-amber-950/20' 
            : 'border-border hover:bg-muted/50'
        } ${isParentDisabled ? 'opacity-50' : ''}`}
      >
        <div className="hidden lg:flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isEnabled ? 'bg-[#2563eb]/10' : 'bg-slate-100 dark:bg-slate-800'
          }`}>
            <Icon className={`w-5 h-5 ${isEnabled ? 'text-[#2563eb]' : 'text-slate-400'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-medium">{flag.display_name}</h4>
              {hasChange && (
                <Badge className="bg-amber-500 text-white text-xs">Modified</Badge>
              )}
              {flag.parent_feature && (
                <Badge variant="outline" className="text-xs">
                  Requires: {flags.find(f => f.name === flag.parent_feature)?.display_name}
                </Badge>
              )}
              {isLockedAlwaysOn && (
                <Badge className="bg-emerald-600 text-white text-xs">Always On</Badge>
              )}
            </div>
            {flag.description && (
              <p className="text-sm text-muted-foreground">{flag.description}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">ID: {flag.name}</p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={() => handleToggle(flag.name, isEnabled)}
          disabled={isParentDisabled || isLockedAlwaysOn}
          data-testid={`feature-toggle-${flag.name}`}
          className="data-[state=checked]:bg-[#2563eb]"
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
      </div>
    );
  }

  const changesCount = Object.keys(pendingChanges).length;

  return (
    <div className="space-y-6" data-testid="feature-management-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#2563eb]/10">
            <Sliders className="w-6 h-6 text-[#2563eb]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Feature Management</h1>
            <p className="text-sm text-muted-foreground">Enable or disable pages, modules, and features</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchFlags} disabled={saving}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleResetDefaults} disabled={saving} className="text-red-600">
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset Defaults
          </Button>
        </div>
      </div>

      {/* Pending Changes Banner */}
      {changesCount > 0 && (
        <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Badge className="bg-amber-500">{changesCount}</Badge>
              <span className="font-medium">Unsaved changes</span>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setPendingChanges({})}
                disabled={saving}
              >
                Discard
              </Button>
              <Button 
                onClick={handleSaveChanges} 
                disabled={saving}
                className="bg-[#2563eb] hover:bg-[#1d4ed8]"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feature Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-muted/40 p-1">
          {orderedCategories.map(category => {
            const Icon = categoryIcons[category] || Settings;
            return (
              <TabsTrigger key={category} value={category} className="gap-2 text-xs min-w-[180px] justify-center" data-testid={`feature-tab-${category}`}>
                <Icon className="w-4 h-4 hidden sm:block" />
                {categoryLabels[category] || category}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {orderedCategories.map((category) => {
          const categoryFlags = flagsByCategory[category] || [];
          return (
          <TabsContent key={category} value={category} className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {React.createElement(categoryIcons[category] || Settings, { className: "w-5 h-5" })}
                  {categoryLabels[category] || category}
                </CardTitle>
                <CardDescription>
                  {categoryDescriptions[category] || 'Manage global feature flag availability'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {categoryFlags.map(flag => React.cloneElement(renderFeatureCard(flag), { key: flag.name || flag.id }))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        )})}
      </Tabs>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold">{flags.filter(f => f.is_enabled).length}</p>
            <p className="text-xs text-muted-foreground">Enabled</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <XCircle className="w-6 h-6 mx-auto mb-2 text-red-500" />
            <p className="text-2xl font-bold">{flags.filter(f => !f.is_enabled).length}</p>
            <p className="text-xs text-muted-foreground">Disabled</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Map className="w-6 h-6 mx-auto mb-2 text-[#2563eb]" />
            <p className="text-2xl font-bold">{flags.filter(f => f.category === 'map_feature' && f.is_enabled).length}</p>
            <p className="text-xs text-muted-foreground">Map Features</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Bell className="w-6 h-6 mx-auto mb-2 text-amber-500" />
            <p className="text-2xl font-bold">{flags.filter(f => f.category === 'integration' && f.is_enabled).length}</p>
            <p className="text-xs text-muted-foreground">Integrations</p>
          </CardContent>
        </Card>
      </div>

      {/* Reset Confirmation Dialog */}
      <Dialog open={!!pendingFeatureAction} onOpenChange={(open) => { if (!open) setPendingFeatureAction(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" />{pendingFeatureAction?.title}</DialogTitle>
            <DialogDescription>{pendingFeatureAction?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setPendingFeatureAction(null)}>Cancel</Button>
            <Button size="sm" variant="destructive" onClick={async () => { await pendingFeatureAction?.action(); setPendingFeatureAction(null); }}>Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
